import * as React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import map from 'lodash/map'
import moment from 'moment'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import SendEmailReminderModal from 'modules/attendance/components/SendEmailReminderModal'
import type CustomPeriodForm from 'modules/attendance/components/TimeTracking/Layout/form'
import { CUSTOM_PERIOD_TYPES } from 'modules/attendance/components/TimeTracking/Layout/form'
import CreateModal from 'modules/attendance/components/compensation/payroll/create'
import RequestModal, {
  CompensationRequestAction,
} from 'modules/attendance/components/compensation/payroll/request'
import ResponseModal, {
  CompensationReponse,
  CompensationResult,
} from 'modules/attendance/components/compensation/payroll/response'
import { PeriodState } from 'modules/attendance/lib/types'
import compensations from 'modules/attendance/store/collections/compensations'
import employeeStats from 'modules/attendance/store/collections/employeeStats'
import reviews from 'modules/attendance/store/collections/reviews'
import type { Status } from 'modules/attendance/store/models/Period'
import type Period from 'modules/attendance/store/models/Period'

import CompensateButton from './_CompensateButton'
import ConfirmButton from './_ConfirmButton'

export interface Props {
  onDeselectAll: () => void
  payrollCompensation: boolean
  onCompensate: (employeeIds: number[], timestamp: string) => void
  selected: Period[]
  currentTabState: Status
  customPeriodForm: CustomPeriodForm
  canSendEmailReminder: boolean
}

const PeriodsActions = ({
  selected,
  currentTabState,
  customPeriodForm,
  onDeselectAll,
  payrollCompensation,
  onCompensate,
  canSendEmailReminder,
}: Props) => {
  const selectedCount = selected.length
  const disabled = !selectedCount
  const [payrollCompensationModalOpen, setPayrollCompensationModalOpen] = React.useState(false)
  const [payrollCompensationRequestModalOpen, setPayrollCompensationRequestModalOpen] =
    React.useState(false)
  const [payrollCompensationResponseModalOpen, setPayrollCompensationResponseModalOpen] =
    React.useState(false)
  const [response, setResponse] = React.useState<CompensationReponse>({ successes: [], errors: [] })
  const [compensationAction, setCompensationAction] = React.useState<CompensationRequestAction>(
    CompensationRequestAction.CREATE
  )

  const {
    approved: periodStatusApproved,
    pending: periodStatusPending,
    in_progress: periodStatusInProgress,
  } = PeriodState

  const startOn = dateToString(customPeriodForm.startOn, 'backend')
  const endOn = dateToString(customPeriodForm.endOn, 'backend')
  const { periodType } = customPeriodForm

  const [emailReminderModalOpen, toggleEmailReminderModalOpen] = useToggle(false)

  const trackData = {
    from: startOn,
    to: endOn,
    period_type: periodType,
  }

  const label = () => {
    if (disabled) {
      switch (currentTabState) {
        case periodStatusApproved:
          return i18n.t('time_tracking.table.refuse.select')

        default:
          return i18n.t('time_tracking.table.approve.select')
      }
    } else {
      switch (currentTabState) {
        case periodStatusApproved:
          return i18n.tp('time_tracking.table.refuse.selected', {
            count: selectedCount,
          })

        default:
          return i18n.tp('time_tracking.table.approve.selected', {
            count: selectedCount,
          })
      }
    }
  }

  const toggledPeriodStatus = () => {
    switch (currentTabState) {
      case periodStatusApproved:
        return periodStatusPending

      default:
        return periodStatusApproved
    }
  }

  const updateStats = (employeeIds: number[]) => {
    const newState = toggledPeriodStatus()

    if (!employeeStats || !employeeIds.length) return

    employeeIds.forEach((id) => employeeStats?.find({ employee_id: id })?.set({ state: newState }))
  }

  const toggleSelected = () => {
    selected.map((period) =>
      period.set({
        state: toggledPeriodStatus(),
      })
    )
  }

  const onConfirmPeriods = async () => {
    const employeeIds = selected.map((period) => period.get('employee_id'))

    const reviewsData = {
      employee_ids: employeeIds,
      start_on: startOn,
      end_on: endOn,
      period_type: periodType,
    }

    const { success } = await asyncAction(() => {
      if (toggledPeriodStatus() === periodStatusApproved) {
        track(trackingEvents.timeTracking.approvePendingPeriods, trackData)
        return reviews.rpc('bulk_create', reviewsData)
      } else {
        track(trackingEvents.timeTracking.disapproveApprovedPeriods, trackData)
        return reviews.rpc('bulk_destroy', reviewsData)
      }
    })

    if (success) {
      onDeselectAll()
      toggleSelected()

      if (hasFeature('time_tracking_page_with_pagination')) {
        updateStats(employeeIds)
      }
    }
  }

  const onCompensatePeriods = async () => {
    const employeeIds = selected.map((period) => period.get('employee_id'))

    const compensationData = {
      employee_ids: employeeIds,
      start_on: startOn,
      end_on: endOn,
      period: customPeriodForm.payrollPeriodType,
    }

    const { success, value } = await asyncAction(() =>
      compensations.rpc('bulk_create_payroll_supplement', compensationData)
    )

    setCompensationAction(CompensationRequestAction.CREATE)
    setPayrollCompensationModalOpen(false)
    setPayrollCompensationRequestModalOpen(true)

    if (success) {
      setResponse(value)
      setPayrollCompensationRequestModalOpen(false)
      setPayrollCompensationResponseModalOpen(true)
      onCompensate(
        value.successes.map((success: CompensationResult) => success.employee_id),
        moment().format('hh:mm:ss')
      )
    }
  }

  const onDisapprovePeriods = async () => {
    const employeeIds = selected.map((period) => period.get('employee_id'))

    const compensationData = {
      employee_ids: employeeIds,
      start_on: startOn,
      end_on: endOn,
    }

    track(trackingEvents.timeTracking.disapproveApprovedPeriods, trackData)

    const { success, value } = await asyncAction(() =>
      reviews.rpc('bulk_destroy', compensationData)
    )

    setCompensationAction(CompensationRequestAction.DESTROY)
    setPayrollCompensationRequestModalOpen(!!success)

    if (success) {
      setResponse(value)
      setPayrollCompensationRequestModalOpen(false)
      if (value.successes?.length || value.errors?.length) {
        setPayrollCompensationResponseModalOpen(true)
      } else {
        onDeselectAll()
        toggleSelected()
      }
      let successfulEmployeeIds = []

      if (value.successs) {
        successfulEmployeeIds = value.successes.map(
          (compensationResult: CompensationResult) => compensationResult.employee_id
        )
      } else {
        successfulEmployeeIds = map(value, 'employee_id')
      }

      onCompensate(successfulEmployeeIds, moment().format('hh:mm:ss'))
    }
  }

  const onCloseModal = () => {
    setPayrollCompensationResponseModalOpen(false)
    onDeselectAll()
    if (compensationAction === CompensationRequestAction.DESTROY) toggleSelected()
  }

  const onSendReminder = () => {
    toggleEmailReminderModalOpen(true)()
    track(trackingEvents.timeTracking.sendClockInReminderStart, {
      from: 'page',
      user_count: selectedEmployeeIds.length,
    })
  }

  const selectedEmployeeIds = selected.map((s) => s.attributes.employee_id)
  const currentTabIsApproved = currentTabState === periodStatusApproved
  const canCompensatePeriodType =
    periodType === CUSTOM_PERIOD_TYPES.monthly || periodType === CUSTOM_PERIOD_TYPES.weekly
  const showCompensationButton =
    currentTabIsApproved && canCompensatePeriodType && hasFeature('payroll_bundle')
  const showSendEmailReminder = canSendEmailReminder && !currentTabIsApproved
  const showEmailReminderModalOpen = emailReminderModalOpen && showSendEmailReminder

  if (currentTabState === periodStatusInProgress) {
    if (canSendEmailReminder) {
      return (
        <>
          <Button disabled={disabled} onClick={onSendReminder} type="primary">
            {i18n.t('dashboard.who_is_in.reminder_modal.send_reminder')}
          </Button>
          {showEmailReminderModalOpen && (
            // TODO: can we do better than this? Do not reuse random components from
            // other places because it does not make sense.
            <SendEmailReminderModal
              employeeId={0}
              employeeFullName=""
              selectedEmployeeIds={selectedEmployeeIds}
              onClose={toggleEmailReminderModalOpen(false)}
            />
          )}
        </>
      )
    } else {
      return null
    }
  }

  if (!showCompensationButton || !payrollCompensation) {
    return (
      <Box flexDirection="row" flexWrap="noWrap" gap="s8">
        <ConfirmButton label={label()} disabled={disabled} onClick={onConfirmPeriods} />
      </Box>
    )
  }

  return (
    <>
      <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s8">
        <CompensateButton
          disabled={disabled}
          onClick={() => setPayrollCompensationModalOpen(true)}
        />
        <Popover type="list">
          <Popover.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </Popover.Trigger>
          <Popover.Content side="bottom" align="end">
            <Popover.TouchableOption
              title={i18n.t('time_tracking.table.disapprove.title')}
              description={i18n.t('time_tracking.table.disapprove.description')}
              onClick={onDisapprovePeriods}
              disabled={disabled}
            />
          </Popover.Content>
        </Popover>
      </Box>
      {payrollCompensationModalOpen && (
        <CreateModal
          onSubmit={onCompensatePeriods}
          form={customPeriodForm}
          onCloseModal={() => setPayrollCompensationModalOpen(false)}
          selected={selected}
        />
      )}
      {payrollCompensationRequestModalOpen && <RequestModal />}
      {payrollCompensationResponseModalOpen && (
        <ResponseModal
          action={compensationAction}
          response={response}
          selected={selected}
          onCloseModal={onCloseModal}
        />
      )}
    </>
  )
}

export default observer(PeriodsActions)
